/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import MainLayout from '../../components/common/MainLayout';
import type { Farm } from '../../models/Farm';
import {
  useCreateAttributeBindingMutation,
  useCreateCropAttributeMutation,
  useGetAvailableAttributesQuery,
  useGetCropByIdQuery,
  useGetCropAttributeByIdQuery,
  useSetTaxonomyAttributeValueMutation,
  useDeleteCropAttributeBindingMutation,
  useUpdateCropMutation,
  useDeleteCropMutation,
  useGetCropTaxonomyTreeQuery,
} from '../../api/cropsApi';
import {
  useGetPropertiesQuery,
  useGetCropPropertyBindingsQuery,
  useCreateCropPropertyBindingMutation,
  useUpdateCropPropertyBindingMutation,
  useDeleteCropPropertyBindingMutation,
} from '../../api/plantingEventsApi';
import type { CropPropertyBinding } from '../../types/plantingEvents';
import type {
  CropAttributeCreatePayload,
  TaxonomyScope,
} from '../../models/Crop';
import PlantingsList from '../../components/plantings/PlantingsList';
import { toastError, toastSuccess } from '../../utils/toast';
import { CropFormModal } from '../../components/crops/CropFormModal';
import { hasAction } from '../../types/permissions';
import { useRefreshWorld, useSave, useWorld } from '../../contexts/WorldContext';
import { buildScenarioWorldIndexes, findScenarioCropSummary } from '../../utils/scenarioWorldAdapters';

const styles = {
  flexBetween: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }),
  flexBetweenStart: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }),
  noMargin: css({ margin: 0 }),
  descriptionBlock: css({ width: '100%', marginBottom: 16 }),
  smallText: css({ fontSize: '0.85em' }),
  textCenter: css({ marginBottom: 16, textAlign: 'center' }),
  linkButton: css({ padding: 0, height: 'auto' }),
  xsmallText: css({ fontSize: 12 }),
  eventParametersHelp: css({ marginTop: 8 }),
};

const { Title, Text } = Typography;

const ScenarioFarmCropDetailsPage: React.FC = () => {
  const { saveId, cropId } = useParams<{ saveId: string; cropId: string }>();
  const { t } = useTranslation('crops');
  const navigate = useNavigate();
  const world = useWorld();
  const refreshWorld = useRefreshWorld();
  const indexes = useMemo(() => buildScenarioWorldIndexes(world.structure), [world.structure]);
  const crop = useMemo(
    () => findScenarioCropSummary(world.structure, cropId, indexes, world.runtime?.plantings),
    [cropId, indexes, world.runtime?.plantings, world.structure],
  );

  if (!crop) {
    return (
      <MainLayout>
        <Card>{t('details.notFound')}</Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('nav.farms')}</Link> },
            { title: <Link to={`/saves/${saveId}`}>{world.scenario?.title || t('nav.scenario')}</Link> },
            { title: <Link to={`/saves/${saveId}/crops`}>{t('nav.crops')}</Link> },
            { title: crop.name },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Alert
          type="info"
          showIcon
          message={t('details.scenario.title')}
          description={t('details.scenario.description')}
        />

        <Card
          extra={
            <Space>
              <Button onClick={() => navigate(`/saves/${saveId}/crops/${crop.routeId}/plantings`)}>{t('details.plantings')}</Button>
              <Button onClick={() => { void refreshWorld(); }}>{t('common:actions.refresh')}</Button>
            </Space>
          }
        >
          <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
            <div css={styles.flexBetweenStart}>
              <div>
                <Title level={4} css={styles.noMargin}>{crop.name}</Title>
                <Text type="secondary">scenario crop · {crop.routeId}</Text>
              </div>
              <Tag color="blue">{t('details.scenario.quantity', { count: crop.totalQuantity })}</Tag>
            </div>
            <Descriptions size="small" column={2}>
              <Descriptions.Item label={t('details.plantings')}>{crop.plantingsCount || 0}</Descriptions.Item>
              <Descriptions.Item label={t('details.scenario.beds')}>{crop.bedIds.length}</Descriptions.Item>
              <Descriptions.Item label={t('details.scenario.plots')}>{crop.plotIds.length}</Descriptions.Item>
              <Descriptions.Item label={t('details.scenario.dataType')}>Scenario world</Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>

        <Card title={t('details.scenario.cropPlantings')}>
          {crop.plantings.length === 0 ? (
            <Empty description={t('details.scenario.noPlantings')} />
          ) : (
            <PlantingsList
              plantings={crop.plantings}
              groupByCrop={false}
            />
          )}
        </Card>
      </Space>
    </MainLayout>
  );
};

const FarmBoundCropDetailsPage: React.FC = () => {
  const { saveId, cropId } = useParams<{ saveId: string; cropId: string }>();
  const { t } = useTranslation('crops');
  const save = useSave();
  const world = useWorld();
  const farmId = save.farmId;
  const navigate = useNavigate();
  const [customAttributeForm] = Form.useForm();
  const [customAttributeModalOpen, setCustomAttributeModalOpen] = useState(false);
  
  const [editAttributeModalOpen, setEditAttributeModalOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<any>(null);
  const [editingAttributeId, setEditingAttributeId] = useState<number | null>(null);
  const [editAttributeForm] = Form.useForm();

  const [bindAttributeModalOpen, setBindAttributeModalOpen] = useState(false);
  const [bindAttributeForm] = Form.useForm();
  const [selectedAttributeForBinding, setSelectedAttributeForBinding] = useState<number | null>(null);

  // Properties Binding
  const [bindPropertyModalOpen, setBindPropertyModalOpen] = useState(false);
  const [bindPropertyForm] = Form.useForm();
  const [editEventBindingModalOpen, setEditEventBindingModalOpen] = useState(false);
  const [editEventBindingForm] = Form.useForm();
  const [editingEventBinding, setEditingEventBinding] = useState<CropPropertyBinding | null>(null);

  const [editCropModalOpen, setEditCropModalOpen] = useState(false);

  const farm = useMemo<Farm | null>(() => (
    farmId
      ? {
          id: farmId,
          name: save.farmName || t('nav.save'),
          is_owner: world.farm?.isOwner ?? false,
          actions: (world.allowedActions?.global || []).map((code) => ({ title: '', code })),
        }
      : null
  ), [farmId, save.farmName, t, world.allowedActions?.global, world.farm?.isOwner]);
  const farmActions = useMemo(() => farm?.actions ?? [], [farm?.actions]);
  const user = useSelector((state: any) => state.auth.user);
  const userActions = useSelector((state: any) => state.auth.user?.actions || []);

  // Проверка прав на редактирование атрибутов культур
  const canEditCropAttributes = useMemo(() => {
    if (!user) return false;
    // Суперадмин/суперюзер
    if (user.role && ['superadmin', 'superuser'].includes(String(user.role).toLowerCase())) {
      return true;
    }
    // Глобальные права
    if (Array.isArray(userActions)) {
      const hasGlobalPermission = 
        userActions.includes('crop_attributes.manage') ||
        userActions.includes('crops.attributes.manage') ||
        userActions.includes('crops.attributes.update') ||
        userActions.includes('crops.manage') ||
        userActions.includes('crops.update');
      if (hasGlobalPermission) return true;
    }
    // Владелец фермы
    if (farm?.is_owner) return true;
    // Права на уровне фермы
    if (Array.isArray(farmActions)) {
      const hasFarmPermission = farmActions.some((action: any) => {
        const code = typeof action === 'string' ? action : action.code;
        return (
          code === 'crop_attributes.manage' ||
          code === 'crops.attributes.manage' ||
          code === 'crops.attributes.update' ||
          code === 'crops.manage' ||
          code === 'crops.update' ||
          code === 'farms.update'
        );
      });
      if (hasFarmPermission) return true;
    }
    return false;
  }, [user, userActions, farm, farmActions]);

  const cropQueryArg = cropId ? cropId : skipToken;
  const {
    data: crop,
    isLoading: cropLoading,
    isError: cropError,
    refetch: refetchCrop,
  } = useGetCropByIdQuery(cropQueryArg);

  // Используем атрибуты из crop.attributes вместо отдельных запросов
  const cropAttributes = useMemo(() => crop?.attributes || [], [crop?.attributes]);

  // Загружаем доступные атрибуты для привязки с учетом таксономии (save-scoped).
  const {
    data: availableAttributesFromApi = [],
    isLoading: availableAttributesLoading,
  } = useGetAvailableAttributesQuery(
    cropId && saveId
      ? { scope: 'crop', scopeId: cropId, saveId }
      : skipToken,
  );

  // Атрибуты, которые еще не привязаны к этой культуре
  const availableAttributes = useMemo(() => {
    const boundAttributeIds = new Set(cropAttributes.map((attr) => attr.attributeId));
    return availableAttributesFromApi.filter((attr) => !boundAttributeIds.has(attr.id));
  }, [availableAttributesFromApi, cropAttributes]);

  const [createAttributeBinding, { isLoading: bindingAttribute }] = useCreateAttributeBindingMutation();
  const [setTaxonomyAttributeValue, { isLoading: isUpdatingValue }] = useSetTaxonomyAttributeValueMutation();
  const [createCropAttribute, { isLoading: creatingCustomAttribute }] = useCreateCropAttributeMutation();

  // Properties API hooks
  const { data: properties = [], isLoading: propertiesLoading } = useGetPropertiesQuery('planting');
  const { data: cropEventBindings = [], isLoading: bindingsLoading } = useGetCropPropertyBindingsQuery(
    cropId
      ? { cropId, farmId }
      : skipToken
  );
  const [createEventBinding, { isLoading: creatingEventBinding }] = useCreateCropPropertyBindingMutation();
  const [updateEventBinding, { isLoading: updatingEventBinding }] = useUpdateCropPropertyBindingMutation();
  const [deleteEventBinding] = useDeleteCropPropertyBindingMutation();
  const [deleteCropAttributeBinding] = useDeleteCropAttributeBindingMutation();
  const [updateCrop, { isLoading: updatingCrop }] = useUpdateCropMutation();
  const [deleteCrop] = useDeleteCropMutation();
  
  const { data: taxonomy, isLoading: taxonomyLoading } = useGetCropTaxonomyTreeQuery();
  
  const canUpdateCrops = hasAction(user, 'crops', 'crops.update', farm?.id);
  const canDeleteCrops = hasAction(user, 'crops', 'crops.delete', farm?.id);
  
  const { data: fullAttribute } = useGetCropAttributeByIdQuery(
    editingAttributeId != null && saveId
      ? { id: editingAttributeId, saveId }
      : skipToken
  );

  const { data: fullAttributeForBinding } = useGetCropAttributeByIdQuery(
    selectedAttributeForBinding != null && saveId
      ? { id: selectedAttributeForBinding, saveId }
      : skipToken
  );

  const handleCreateCustomAttribute = async () => {
    if (!cropId) {
      toastError(t('details.errors.noCrop'));
      return;
    }

    try {
      if (!saveId) {
        toastError(t('details.errors.noSave'));
        return;
      }
      const values = await customAttributeForm.validateFields();
      const payload: CropAttributeCreatePayload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        scopeType: 'save',
        options: null,
      };

      const created = await createCropAttribute({ ...payload, saveId }).unwrap();
      const value = values.value?.trim() ?? '';
      if (value) {
        await setTaxonomyAttributeValue({
          scope: 'crop',
          scopeId: cropId,
          attributeId: created.id,
          saveId,
          choiceId: null,
          customValue: value,
        }).unwrap();
      }

      toastSuccess(t('details.toasts.attributeAdded'));
      setCustomAttributeModalOpen(false);
      customAttributeForm.resetFields();
      await refetchCrop();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleOpenEditAttribute = (attr: any) => {
    setEditingAttribute(attr);
    setEditingAttributeId(attr.attributeId);
    
    if (attr.choiceId != null) {
      editAttributeForm.setFieldsValue({ value: { choiceId: String(attr.choiceId) } });
    } else if (attr.customValue) {
      editAttributeForm.setFieldsValue({ value: { customValue: attr.customValue } });
    } else if (attr.value) {
      editAttributeForm.setFieldsValue({ value: { customValue: attr.value } });
    } else {
      editAttributeForm.setFieldsValue({ value: {} });
    }
    
    setEditAttributeModalOpen(true);
  };

  const handleSaveAttributeValue = async () => {
    if (!cropId || !editingAttribute) return;
    
    try {
      const values = await editAttributeForm.validateFields();
      const hasOptions = fullAttribute && Array.isArray(fullAttribute.options) && fullAttribute.options.length > 0;
      
      const payload: any = {
        scope: 'crop' as TaxonomyScope,
        scopeId: cropId,
        attributeId: editingAttribute.attributeId,
        farmId,
      };
      
      if (hasOptions) {
        const choiceId = values.value?.choiceId;
        payload.choiceId = choiceId ? (isNaN(Number(choiceId)) ? choiceId : Number(choiceId)) : null;
        payload.customValue = null;
      } else {
        payload.choiceId = null;
        payload.customValue = values.value?.customValue?.trim() || null;
      }
      
      await setTaxonomyAttributeValue(payload).unwrap();
      toastSuccess(t('details.toasts.attributeValueUpdated'));
      setEditAttributeModalOpen(false);
      setEditingAttribute(null);
      setEditingAttributeId(null);
      editAttributeForm.resetFields();
      await refetchCrop();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleBindAttribute = async () => {
    if (!cropId) {
      toastError(t('details.errors.noCrop'));
      return;
    }

    try {
      if (!saveId) {
        toastError(t('details.errors.noSave'));
        return;
      }
      const values = await bindAttributeForm.validateFields();
      const attributeId = values.attributeId;

      // Создаем привязку
      await createAttributeBinding({
        attributeId,
        scope: 'crop',
        scopeId: cropId,
        saveId,
      }).unwrap();

      // Если указано значение, устанавливаем его
      const hasOptions = fullAttributeForBinding && Array.isArray(fullAttributeForBinding.options) && fullAttributeForBinding.options.length > 0;
      const hasValue = hasOptions
        ? values.value?.choiceId != null
        : values.value?.customValue != null && values.value.customValue.trim() !== '';

      if (hasValue) {
        const valuePayload: any = {
          scope: 'crop' as TaxonomyScope,
          scopeId: cropId,
          attributeId,
          saveId,
        };
        
        if (hasOptions) {
          const choiceId = values.value?.choiceId;
          valuePayload.choiceId = choiceId ? (isNaN(Number(choiceId)) ? choiceId : Number(choiceId)) : null;
          valuePayload.customValue = null;
        } else {
          valuePayload.choiceId = null;
          valuePayload.customValue = values.value?.customValue?.trim() || null;
        }
        
        await setTaxonomyAttributeValue(valuePayload).unwrap();
      }

      toastSuccess(t('details.toasts.attributeBound'));
      setBindAttributeModalOpen(false);
      bindAttributeForm.resetFields();
      setSelectedAttributeForBinding(null);
      await refetchCrop();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  // Property Bindings handlers
  const availableProperties = useMemo(() => {
    const boundIds = new Set(cropEventBindings.map((b) => b.propertyId));
    return properties.filter((attr) => attr.semanticType === 'measured' && !boundIds.has(attr.id));
  }, [properties, cropEventBindings]);

  const handleBindProperty = async () => {
    if (!cropId) {
      toastError(t('details.errors.noCrop'));
      return;
    }

    try {
      const values = await bindPropertyForm.validateFields();
      await createEventBinding({
        cropId,
        payload: {
          propertyId: values.propertyId,
          isRequired: values.isRequired ?? false,
          includeInPricelist: values.includeInPricelist ?? false,
          sortOrder: values.sortOrder ?? 0,
        },
      }).unwrap();

      toastSuccess(t('details.toasts.propertyBound'));
      setBindPropertyModalOpen(false);
      bindPropertyForm.resetFields();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleEditEventBinding = (binding: CropPropertyBinding) => {
    setEditingEventBinding(binding);
    editEventBindingForm.setFieldsValue({
      isRequired: binding.isRequired,
      includeInPricelist: binding.includeInPricelist,
      sortOrder: binding.sortOrder,
    });
    setEditEventBindingModalOpen(true);
  };

  const handleUpdateEventBinding = async () => {
    if (!cropId || !editingEventBinding) {
      return;
    }

    try {
      const values = await editEventBindingForm.validateFields();
      await updateEventBinding({
        cropId,
        bindingId: editingEventBinding.id,
        payload: {
          isRequired: values.isRequired,
          includeInPricelist: values.includeInPricelist,
          sortOrder: values.sortOrder,
        },
      }).unwrap();

      toastSuccess(t('details.toasts.bindingUpdated'));
      setEditEventBindingModalOpen(false);
      setEditingEventBinding(null);
      editEventBindingForm.resetFields();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleDeleteEventBinding = async (binding: CropPropertyBinding) => {
    if (!cropId) return;

    Modal.confirm({
      title: t('details.deletePropertyBinding.title'),
      content: t('details.deletePropertyBinding.content', { name: binding.property.name }),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          await deleteEventBinding({
            cropId,
            bindingId: binding.id,
          }).unwrap();
          toastSuccess(t('details.toasts.bindingDeleted'));
        } catch (error: any) {
          toastError(error);
        }
      },
    });
  };

  const handleEditCrop = () => {
    setEditCropModalOpen(true);
  };

  const handleCropUpdate = async (values: any) => {
    if (!cropId) return;

    const speciesId = Number(values.speciesId);
    const cultivarId =
      values.cultivarId === undefined || values.cultivarId === null
        ? null
        : Number(values.cultivarId);

    try {
      await updateCrop({
        id: cropId,
        payload: {
          speciesId,
          cultivarId,
          latinName: values.latinName,
          name: values.name,
        },
      }).unwrap();
      toastSuccess(t('details.toasts.cropUpdated'));
      setEditCropModalOpen(false);
      await refetchCrop();
    } catch (error: any) {
      // Error handled by toast
    }
  };

  const handleDeleteCrop = () => {
    Modal.confirm({
      title: t('details.deleteCrop.title', { name: crop?.name }),
      content: t('details.deleteCrop.content'),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          await deleteCrop(cropId!).unwrap();
          toastSuccess(t('details.toasts.cropDeleted'));
          navigate(`/saves/${saveId}/crops`);
        } catch (error: any) {
          toastError(error);
        }
      },
    });
  };

  const handleDeleteCropAttributeBinding = async (attr: any) => {
    if (!cropId) return;

    // Можно удалить только привязки на уровне культуры (cropId есть и bindingId есть)
    if (!attr.cropId || !attr.bindingId) {
      toastError(t('details.errors.onlyCropLevelBindings'));
      return;
    }

    Modal.confirm({
      title: t('details.deleteAttributeBinding.title'),
      content: t('details.deleteAttributeBinding.content', { name: attr.name }),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          await deleteCropAttributeBinding({
            cropId,
            bindingId: attr.bindingId,
          }).unwrap();
          toastSuccess(t('details.toasts.attributeBindingDeleted'));
        } catch (error: any) {
          toastError(error);
        }
      },
    });
  };

  const renderAttributeCard = (attr: any) => {
    const key = String(attr.attributeId);
    const scopeLabel = attr.scope ? t(`scope.${attr.scope as TaxonomyScope}`) : '—';
    const currentValue = attr.value || '—';
    const isCropLevelBinding = !!attr.cropId && !!attr.bindingId;

    return (
      <Descriptions.Item key={key} label={attr.name} span={3}>
        <div css={styles.flexBetween}>
          <Space size={4}>
            <Text>{currentValue}</Text>
            {attr.cropId ? (
              <Tag color="purple" css={styles.noMargin}>{t('scope.crop')}</Tag>
            ) : (
              <Tag color="cyan" css={styles.noMargin}>{scopeLabel}</Tag>
            )}
          </Space>
          {canEditCropAttributes && (
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenEditAttribute(attr)}
              >
                {t('common:actions.edit')}
              </Button>
              {isCropLevelBinding && (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteCropAttributeBinding(attr)}
                >
                  {t('details.unbind')}
                </Button>
              )}
            </Space>
          )}
        </div>
      </Descriptions.Item>
    );
  };

  const farmName = farm?.name ?? (farmId ? t('nav.farmWithId', { id: farmId }) : t('nav.farm'));

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('nav.farms')}</Link> },
            { title: <Link to={`/saves/${saveId}`}>{farmName}</Link> },
            { title: <Link to={`/saves/${saveId}/crops`}>{t('nav.crops')}</Link> },
            { title: crop?.name ?? t('scope.crop') },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          {cropLoading ? (
            <Spin />
          ) : cropError || !crop ? (
            <Empty description={t('details.notFound')} />
          ) : (
            <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
              <div css={styles.flexBetweenStart}>
                <div>
                  <Title level={4} css={styles.noMargin}>
                    {crop.name}
                  </Title>
                  <Text type="secondary" italic>{crop.latinName}</Text>
                </div>
                <Space>
                  {canUpdateCrops && (
                    <Button icon={<EditOutlined />} onClick={handleEditCrop}>
                      {t('common:actions.edit')}
                    </Button>
                  )}
                  {canDeleteCrops && (
                    <Button danger icon={<DeleteOutlined />} onClick={handleDeleteCrop}>
                      {t('common:actions.delete')}
                    </Button>
                  )}
                </Space>
              </div>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label={t('scope.species')}>{crop.speciesName || crop.speciesId || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('scope.cultivar')}>{crop.cultivarName || crop.cultivarId || '—'}</Descriptions.Item>
              </Descriptions>
              {canEditCropAttributes && (
                <Space wrap>
                  <Button onClick={() => navigate(`/saves/${saveId}/crops`)}>{t('details.backToList')}</Button>
                </Space>
              )}
            </Space>
          )}
        </Card>

        <Card 
          title={t('details.attributes.title')}
          extra={
            canEditCropAttributes && (
              <Button
                size="small"
                onClick={() => {
                  bindAttributeForm.resetFields();
                  setSelectedAttributeForBinding(null);
                  setBindAttributeModalOpen(true);
                }}
                disabled={cropLoading || !crop || availableAttributes.length === 0}
              >
                {t('details.attributes.bind')}
              </Button>
            )
          }
        >
          <Space direction="vertical" size={8} css={styles.descriptionBlock}>
            <Text type="secondary">
              <Trans i18nKey="crops:details.attributes.help" components={{ b: <strong /> }} />
            </Text>
          </Space>
          {cropLoading ? (
            <Spin />
          ) : cropAttributes.length === 0 ? (
            <Empty description={t('details.attributes.empty')} />
          ) : (
            <Descriptions bordered size="small" column={1}>
              {cropAttributes.map((attr) => renderAttributeCard(attr))}
            </Descriptions>
          )}
        </Card>

        <Card 
          title={t('details.properties.title')}
          extra={
            canEditCropAttributes && (
              <Button
                size="small"
                onClick={() => {
                  bindPropertyForm.resetFields();
                  setBindPropertyModalOpen(true);
                }}
                disabled={cropLoading || !crop || availableProperties.length === 0}
              >
                {t('details.properties.bind')}
              </Button>
            )
          }
        >
          <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
            <Text type="secondary">
              <Trans i18nKey="crops:details.properties.help" components={{ b: <strong /> }} />
            </Text>
            
            {bindingsLoading ? (
              <Spin />
            ) : cropEventBindings.length === 0 ? (
              <Empty description={t('details.properties.empty')} />
            ) : (
              <Descriptions bordered size="small" column={1}>
                {[...cropEventBindings]
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((binding) => (
                    <Descriptions.Item 
                      key={binding.id} 
                      label={
                        <Space size={4}>
                          <Text>{binding.property.name}</Text>
                          {binding.property.unit && (
                            <Text type="secondary" css={styles.smallText}>
                              ({binding.property.unit})
                            </Text>
                          )}
                        </Space>
                      }
                      span={3}
                    >
                      <div css={styles.flexBetween}>
                        <Space size={4}>
                          {binding.isRequired && <Tag color="red">{t('details.properties.requiredTag')}</Tag>}
                          {binding.includeInPricelist && <Tag color="green">{t('details.properties.inPricelistTag')}</Tag>}
                          <Text type="secondary" css={styles.smallText}>
                            {t('details.properties.code', { code: binding.property.code })}
                          </Text>
                        </Space>
                        {canEditCropAttributes && (
                          <Space size={8}>
                            <Button
                              type="link"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => handleEditEventBinding(binding)}
                            >
                              {t('details.properties.configure')}
                            </Button>
                            <Button
                              type="link"
                              size="small"
                              danger
                              onClick={() => handleDeleteEventBinding(binding)}
                            >
                              {t('common:actions.delete')}
                            </Button>
                          </Space>
                        )}
                      </div>
                    </Descriptions.Item>
                  ))}
              </Descriptions>
            )}

            <Text type="secondary" css={styles.eventParametersHelp}>
              <Trans i18nKey="crops:details.properties.howTo" components={{ b: <strong /> }} />
            </Text>
          </Space>
        </Card>
      </Space>

      {/* Модальное окно для привязки атрибута */}
      <Modal
        title={t('details.bindAttributeModal.title')}
        open={bindAttributeModalOpen}
        onOk={handleBindAttribute}
        onCancel={() => {
          setBindAttributeModalOpen(false);
          bindAttributeForm.resetFields();
          setSelectedAttributeForBinding(null);
        }}
        okText={t('details.bindAction')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={bindingAttribute}
      >
        <Form form={bindAttributeForm} layout="vertical" preserve={false}>
          <Form.Item
            label={t('details.bindAttributeModal.attribute')}
            name="attributeId"
            rules={[{ required: true, message: t('details.bindAttributeModal.attributeRequired') }]}
          >
            <Select
              placeholder={t('details.bindAttributeModal.attributeRequired')}
              loading={availableAttributesLoading}
              showSearch
              optionFilterProp="label"
              onChange={(value) => setSelectedAttributeForBinding(value)}
              options={availableAttributes.map((attr) => ({
                value: attr.id,
                label: attr.description ? `${attr.name} — ${attr.description}` : attr.name,
              }))}
            />
          </Form.Item>
          {selectedAttributeForBinding && fullAttributeForBinding && (
            <>
              {Array.isArray(fullAttributeForBinding.options) && fullAttributeForBinding.options.length > 0 ? (
                <Form.Item
                  label={t('details.valueOptional')}
                  name={['value', 'choiceId']}
                >
                  <Select
                    placeholder={t('details.selectValue')}
                    allowClear
                    optionFilterProp="label"
                    options={fullAttributeForBinding.options.map((option: any) => ({
                      value: String(option.id),
                      label: option.code && option.code !== option.title
                        ? `${option.title} (${option.code})`
                        : option.title,
                    }))}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  label={t('details.valueOptional')}
                  name={['value', 'customValue']}
                >
                  <Input placeholder={t('details.enterValue')} allowClear />
                </Form.Item>
              )}
              {fullAttributeForBinding.description && (
                <Typography.Text type="secondary" css={commonStyles.smallText}>
                  {fullAttributeForBinding.description}
                </Typography.Text>
              )}
            </>
          )}
        </Form>
        <div css={styles.textCenter}>
            <Text type="secondary">
              {t('details.bindAttributeModal.notFound')}{' '}
              <Button
                type="link"
                css={styles.linkButton}
                onClick={() => {
                  setBindAttributeModalOpen(false);
                  bindAttributeForm.resetFields();
                  setSelectedAttributeForBinding(null);
                  customAttributeForm.resetFields();
                  setCustomAttributeModalOpen(true);
                }}
              >
                {t('details.bindAttributeModal.createNew')}
              </Button>
            </Text>
          </div>
      </Modal>

      {/* Модальное окно для редактирования значения атрибута */}
      <Modal
        title={t('details.editValueModal.title', { name: fullAttribute?.name ?? editingAttribute?.name ?? '' })}
        open={editAttributeModalOpen}
        onOk={handleSaveAttributeValue}
        onCancel={() => {
          setEditAttributeModalOpen(false);
          editAttributeForm.resetFields();
          setEditingAttribute(null);
          setEditingAttributeId(null);
        }}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={isUpdatingValue}
      >
        <Form form={editAttributeForm} layout="vertical" preserve={false}>
          {fullAttribute && Array.isArray(fullAttribute.options) && fullAttribute.options.length > 0 ? (
            <Form.Item
              label={t('details.value')}
              name={['value', 'choiceId']}
              rules={[{ required: true, message: t('details.selectValue') }]}
            >
              <Select
                placeholder={t('details.selectValue')}
                allowClear
                optionFilterProp="label"
                options={fullAttribute.options.map((option: any) => ({
                  value: String(option.id),
                  label: option.code && option.code !== option.title
                    ? `${option.title} (${option.code})`
                    : option.title,
                }))}
              />
            </Form.Item>
          ) : (
            <Form.Item
              label={t('details.value')}
              name={['value', 'customValue']}
              rules={[{ required: true, message: t('details.enterValue') }]}
            >
              <Input placeholder={t('details.enterValue')} allowClear />
            </Form.Item>
          )}
          {fullAttribute?.description && (
            <Typography.Text type="secondary" css={styles.xsmallText}>
              {fullAttribute.description}
            </Typography.Text>
          )}
        </Form>
      </Modal>

      <Modal
        title={t('details.createAttributeModal.title')}
        open={customAttributeModalOpen}
        onOk={handleCreateCustomAttribute}
        onCancel={() => {
          setCustomAttributeModalOpen(false);
          customAttributeForm.resetFields();
        }}
        okText={t('common:actions.create')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={creatingCustomAttribute}
      >
        <Form form={customAttributeForm} layout="vertical" preserve={false}>
          <Form.Item
            label={t('details.createAttributeModal.name')}
            name="name"
            rules={[{ required: true, message: t('details.createAttributeModal.nameRequired') }]}
          >
            <Input placeholder={t('details.createAttributeModal.namePlaceholder')} />
          </Form.Item>
          <Form.Item label={t('common:labels.description')} name="description">
            <Input.TextArea rows={3} placeholder={t('details.createAttributeModal.descriptionPlaceholder')} allowClear />
          </Form.Item>
          <Form.Item
            label={t('details.value')}
            name="value"
            rules={[{ required: true, message: t('details.createAttributeModal.valueRequired') }]}
          >
            <Input placeholder={t('details.enterValue')} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно для привязки property */}
      <Modal
        title={t('details.bindPropertyModal.title')}
        open={bindPropertyModalOpen}
        onOk={handleBindProperty}
        onCancel={() => {
          setBindPropertyModalOpen(false);
          bindPropertyForm.resetFields();
        }}
        okText={t('details.bindAction')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={creatingEventBinding}
      >
        <Form form={bindPropertyForm} layout="vertical" preserve={false}>
          <Form.Item
            label={t('details.bindPropertyModal.property')}
            name="propertyId"
            rules={[{ required: true, message: t('details.bindPropertyModal.propertyRequired') }]}
          >
            <Select
              placeholder={t('details.bindPropertyModal.propertyRequired')}
              loading={propertiesLoading}
              showSearch
              optionFilterProp="label"
              options={availableProperties.map((attr) => ({
                value: attr.id,
                label: `${attr.name}${attr.unitId ? ` (${attr.unitId})` : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            label={t('details.bindPropertyModal.required')}
            name="isRequired"
            valuePropName="checked"
            tooltip={t('details.bindPropertyModal.requiredTooltip')}
          >
            <Select
              options={[
                { value: false, label: t('common:actions.no') },
                { value: true, label: t('common:actions.yes') },
              ]}
              defaultValue={false}
            />
          </Form.Item>
          <Form.Item
            label={t('details.bindPropertyModal.includeInPricelist')}
            name="includeInPricelist"
            valuePropName="checked"
            tooltip={t('details.bindPropertyModal.includeInPricelistTooltip')}
          >
            <Select
              options={[
                { value: false, label: t('common:actions.no') },
                { value: true, label: t('common:actions.yes') },
              ]}
              defaultValue={false}
            />
          </Form.Item>
          <Form.Item
            label={t('details.bindPropertyModal.sortOrder')}
            name="sortOrder"
            tooltip={t('details.bindPropertyModal.sortOrderTooltip')}
          >
            <Input type="number" placeholder="0" defaultValue={0} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно для редактирования настроек привязки */}
      <Modal
        title={t('details.bindingSettingsModal.title', { name: editingEventBinding?.property.name ?? '' })}
        open={editEventBindingModalOpen}
        onOk={handleUpdateEventBinding}
        onCancel={() => {
          setEditEventBindingModalOpen(false);
          setEditingEventBinding(null);
          editEventBindingForm.resetFields();
        }}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={updatingEventBinding}
      >
        <Form form={editEventBindingForm} layout="vertical" preserve={false}>
          <Form.Item
            label={t('details.bindPropertyModal.required')}
            name="isRequired"
            tooltip={t('details.bindPropertyModal.requiredTooltip')}
          >
            <Select
              options={[
                { value: false, label: t('common:actions.no') },
                { value: true, label: t('common:actions.yes') },
              ]}
            />
          </Form.Item>
          <Form.Item
            label={t('details.bindPropertyModal.includeInPricelist')}
            name="includeInPricelist"
            tooltip={t('details.bindPropertyModal.includeInPricelistTooltip')}
          >
            <Select
              options={[
                { value: false, label: t('common:actions.no') },
                { value: true, label: t('common:actions.yes') },
              ]}
            />
          </Form.Item>
          <Form.Item
            label={t('details.bindPropertyModal.sortOrder')}
            name="sortOrder"
            tooltip={t('details.bindPropertyModal.sortOrderTooltip')}
          >
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>

      <CropFormModal
        open={editCropModalOpen}
        editingCrop={crop || null}
        taxonomy={taxonomy}
        taxonomyLoading={taxonomyLoading}
        onSubmit={handleCropUpdate}
        onCancel={() => setEditCropModalOpen(false)}
        isLoading={updatingCrop}
      />
    </MainLayout>
  );
};

const FarmCropDetailsPage: React.FC = () => {
  const save = useSave();
  if (save.type === 'scenario') {
    return <ScenarioFarmCropDetailsPage />;
  }
  return <FarmBoundCropDetailsPage />;
};

export default FarmCropDetailsPage;
